'use client'

import { memo } from 'react'

import { useTranslations } from 'next-intl'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useRouter } from '@/i18n/navigation'
import type { Track } from '@/lib/api/mock/voice/types'
import { writeUseVoicePayload } from '@/lib/voice/use-voice-payload'

import { AudioOrb } from './audio-orb'
import { VoiceIcon } from './icon'
import { WaveBars } from './wave-bars'

interface AudioCardProps {
  track: Track
  /** 外部传入的真实时长（秒），优先于 track.duration */
  durationSec?: number
  onPlay?: (trackId: number) => void
  isPlaying?: boolean
  progress?: number
}

/**
 * Plaza/My Library 列表中的音频卡片
 *
 * 布局：左封面 · 标题/作者/Tag · 波形 · 播放数/操作
 */
export const AudioCard = memo(function AudioCard({
  track,
  onPlay,
  isPlaying,
  progress = 0,
}: AudioCardProps) {
  const tProfile = useTranslations('voice.clone.profile')
  const tOutput = useTranslations('voice.design.output')
  const router = useRouter()

  const handleUseVoice = (e: React.MouseEvent) => {
    e.stopPropagation()
    const audioUrl = (track as Track & { audioUrl?: string }).audioUrl || ''
    writeUseVoicePayload({
      mode: 'upload',
      voiceName: track.title,
      audioUrl,
    })
    router.push('/voice/clone' as any)
  }

  return (
    <div className="flex items-center gap-3 rounded-[var(--voice-r-lg)] border border-[var(--border)] bg-[var(--surface)] px-3 py-3 transition-[border-color,background] duration-200 hover:border-[var(--border-active)] hover:bg-[var(--surface-2)]">
      {/* 左侧：AudioOrb（圆形渐变环 + 波形 glyph） */}
      <AudioOrb playing={isPlaying} seed={track.seed} size={48} />

      {/* 标题 + 描述 */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className="truncate text-[14px] leading-tight font-semibold text-[var(--text-1)]"
          title={track.title}
        >
          {track.title}
        </div>
        <div
          className="truncate text-[12px] text-[var(--text-3)]"
          title={track.description || track.tag}
        >
          {track.author ? `${track.author} | ` : ''}
          {track.description || track.tag}
        </div>
      </div>

      {/* 波形：纯展示，不再响应点击 */}
      <div className="block min-w-0 flex-1 text-left">
        <WaveBars
          active={isPlaying}
          bars={43}
          height={22}
          playing={isPlaying}
          progress={isPlaying ? progress : 0}
          seed={track.seed}
        />
      </div>

      {/* 右侧：播放数 · 操作 */}
      <div className="flex shrink-0 items-center gap-2">
        {track.plays && (
          <span className="hidden text-[12px] whitespace-nowrap text-[var(--text-3)] md:inline">
            {track.plays}
          </span>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={isPlaying ? tOutput('pause') : tOutput('play')}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-white/[0.06] text-[var(--text-1)] transition-colors hover:bg-white/[0.12] active:scale-95"
              type="button"
              onClick={e => {
                e.stopPropagation()
                onPlay?.(track.id)
              }}
            >
              <VoiceIcon name={isPlaying ? 'pause' : 'play'} size={16} stroke={1.6} />
            </button>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>
            {isPlaying ? tOutput('pause') : tOutput('play')}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={tProfile('useVoice')}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-white/[0.06] text-[var(--text-1)] transition-colors hover:bg-white/[0.12] active:scale-95"
              type="button"
              onClick={handleUseVoice}
            >
              <VoiceIcon name="useVoice" size={16} stroke={1.6} />
            </button>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>{tProfile('useVoice')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
})
